import { Component, CSSProperties, createRef } from 'react';

import { FITTED_TOP_FALLBACK_PX, measureFittedTop, publishFittedTop } from '../../utils/workspace-mode';

/**
 * The shell's TOP OFFSET, published on the fitted element itself (not on <html>) so two Studios on
 * one page -- a spec, a storybook -- cannot fight over it, and read by `studio.module.scss`
 * `.fitted` / `.phone`.
 *
 * Round 3 (ROUND3_BRIEF.md R8): the value is ZERO. The shell used to measure its distance from the
 * document top (site nav + page wrapper + card padding, 65 px once S-02 removed the furniture) and
 * size itself `100dvh - that - anchor`, so the page was exactly one viewport tall and the nav --
 * `position: relative` -- could never scroll away; it read as sticky. Now the shell is
 * `100dvh - anchor`, the page is nav + shell, and the page base scrolls the nav off once when the
 * tab is activated (`utils/workspace-mode.ts#claimViewport`). The 65 px fallback is what a page
 * without scripting shows, and without scripting nothing scrolls, so nav + `100dvh - 65px` fits.
 *
 * Round 13b (R13b-ZZ1, R13 QA studio N4): the value is MEASURED again -- the shell's own top edge in the
 * viewport, floored at 0 (`utils/workspace-mode.ts#measureFittedTop`). Since round 13 (R13-Z2) the shell
 * stands unpinned in the page under the nav, and a 0 made it a full `100dvh` with the nav still on screen,
 * so its last 65/69 px were below the fold (the desktop Music lane, 63 px of every phone pane under the
 * tab bar). Now: nav while the page is at rest, 0 once docked by the snap, pinned or fullscreen -- the box
 * always ends at the fold. The shell re-measures on scroll and resize; the skeleton once, at mount, which
 * is the same number the shell reads a moment later (zero-shift hand-off, S-60).
 *
 * `STUDIO_TOP_FALLBACK_PX` and `utils/workspace-mode.ts#FITTED_TOP_FALLBACK_PX` are the same
 * number and must move together.
 */
export const STUDIO_TOP_VAR = '--xc-studio-top';
export const STUDIO_TOP_FALLBACK_PX = FITTED_TOP_FALLBACK_PX;

/** The `height` both the skeleton and the shell use, so the hand-off between them is zero-shift. */
export const FITTED_HEIGHT = `calc(100dvh - var(${STUDIO_TOP_VAR}, ${STUDIO_TOP_FALLBACK_PX}px) - var(--xc-anchor-h, 0px))`;

/** The header row and the timeline row the shell will draw, reserved as boxes (S-60). */
export const SKELETON_HEADER_PX = 56;
export const SKELETON_TIMELINE_PX = 168;

/**
 * Write `--xc-studio-top: <measured>px` on `el` (R8, round 13b; see `STUDIO_TOP_VAR`). One implementation
 * for both editors: `utils/workspace-mode.ts#publishFittedTop` with the Studio's variable name and the
 * element's own measured top. Silent when there is no window (the skeleton is `ssr: false`, but the guard
 * costs nothing). Returns the value written, for the caller's own bookkeeping.
 */
export function publishStudioTop(el: HTMLElement | null): number {
    const top = measureFittedTop(el);
    publishFittedTop(el, STUDIO_TOP_VAR, top);
    return top;
}

const ROOT_STYLE: CSSProperties = {
    height: FITTED_HEIGHT,
    minHeight: 420,
    boxSizing: 'border-box',
    padding: 8,
    display: 'grid',
    gridTemplateRows: `${SKELETON_HEADER_PX}px minmax(0, 1fr) ${SKELETON_TIMELINE_PX}px`,
    rowGap: 8,
    overflow: 'hidden',
    color: '#94a3b8',
    fontSize: 14,
};

const BLOCK_STYLE: CSSProperties = {
    background: '#fff',
    border: '1px solid #e6ebf1',
    borderRadius: 12,
    boxSizing: 'border-box',
};

/**
 * What stands in the tool container while the Studio chunk downloads — §6.1's `StudioSkeleton`.
 *
 * A CLASS, not the arrow function `single-page-base.component.tsx:24-32` uses for its editors:
 * the owner's ruling is class components, and `next/dynamic`'s `loading` takes any component
 * type, so there is nothing to trade off.
 *
 * S-60: it reserves the SAME box the fitted shell will occupy -- the same `height` expression,
 * the same 8 px padding, a 56 px header row and a 168 px timeline row -- so the hand-off from
 * skeleton to editor moves nothing (R3's < 0.05 budget starts at the first paint of the tab).
 * `--xc-anchor-h` is inside that expression, so the anchor bar's height is reserved too, by the
 * anchor's own measurement. The old skeleton collapsed to 520 px and the shell then took ~1,000,
 * which was the 0.14-0.18 shift `walk-report.json` recorded on load.
 *
 * The rules are inline so this file pulls in no stylesheet and stays out of the Studio's import
 * graph (the page base imports it directly, D10); the shell imports the two constants from HERE
 * for the same reason -- a shared file under `views/` would be an edge the wrong way round.
 */
export class StudioSkeletonComponent extends Component {
    private root = createRef<HTMLDivElement>();

    componentDidMount() {
        publishStudioTop(this.root.current);
    }

    render() {
        return (
            <div
                ref={this.root}
                data-testid="studio-skeleton"
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={ROOT_STYLE}
            >
                <div style={BLOCK_STYLE} data-testid="studio-skeleton-header" />
                <div style={{ ...BLOCK_STYLE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading the studio…
                </div>
                <div style={BLOCK_STYLE} data-testid="studio-skeleton-timeline" />
            </div>
        );
    }
}

export default StudioSkeletonComponent;
